import { type RefObject, useMemo } from 'react';

import { type MenuItemProps, MenuItemType } from '@/components/Menu/types';
import {
  filterStructuredFieldByHiddenLeaves,
  STRUCTURED_FILTER_MENU_SECTIONS,
  useStructuredFilterVisibility,
} from '@/entities/(filter)/common';
import type { OpenParams } from '@/hooks/pure/useModalState';
import {
  ENGAGEMENT_COLUMNS,
  FEEDBACK_COLUMNS,
  type FullColumnKey,
} from '@/types/filterKeyConst';
import { getDropdownPosWithTrigger } from '@/utils/dom';

import useStructuredFilterMap from '../configs/useStructuredFilterMap';

const TWITTER_ONLY_METRIC_COLUMN_KEYS = new Set<string>([
  ENGAGEMENT_COLUMNS.COLLECT_COUNT,
  ENGAGEMENT_COLUMNS.SUBSCRIBERS_COUNT,
]);
export const useStructuredFilterMenus = (props: {
  triggerRef: RefObject<HTMLDivElement>;
  openFieldItemSelect: (
    params?: OpenParams<StructuredField & { valueIndex: number }>,
  ) => void;
  values: StructuredFilterQueryFormatT[];
  disAllowFilter?: FullColumnKey[];
  closeMenu: () => void;
  setSubMenuStatus: (status: string | null) => void;
  subMenuStatus: string | null;
}) => {
  const {
    triggerRef,
    openFieldItemSelect,
    closeMenu,
    setSubMenuStatus,
    subMenuStatus,
    values,
    disAllowFilter,
  } = props;
  const ref = triggerRef;
  const fieldOptions = useStructuredFilterMap();
  const showTwitterMetrics = true;
  const { hiddenLeafIdSet } = useStructuredFilterVisibility();

  const menuOptions: MenuItemProps[] = useMemo(() => {
    const sectionKeys = STRUCTURED_FILTER_MENU_SECTIONS;

    const mapField = (field: StructuredField): MenuItemProps | null => {
      const fieldWithRuntimeVisibility =
        field.columnKey === FEEDBACK_COLUMNS.ENGAGEMENT &&
        field.childField &&
        !showTwitterMetrics
          ? {
              ...field,
              childField: Object.fromEntries(
                Object.entries(field.childField).filter(([columnKey]) => {
                  return !TWITTER_ONLY_METRIC_COLUMN_KEYS.has(columnKey);
                }),
              ),
            }
          : field;

      const visibleField = filterStructuredFieldByHiddenLeaves(
        fieldWithRuntimeVisibility,
        hiddenLeafIdSet,
      );

      if (!visibleField) return null;

      const childField = visibleField.childField;

      return {
        icon: visibleField.icon,
        label: visibleField.columnName,
        onClick: childField
          ? undefined
          : () => {
              const { pos, modifiedHeight } = getDropdownPosWithTrigger(
                ref.current,
              );
              openFieldItemSelect({
                pos: {
                  ...pos,
                  maxHeight: modifiedHeight || undefined,
                },
                modalState: {
                  ...visibleField,
                  valueIndex: values.length,
                },
              });
              closeMenu();
              setSubMenuStatus(null);
            },
        subMenus: childField
          ? Object.values(childField).map((child) => ({
              label: child.columnName,
              onClick: () => {
                const { pos, modifiedHeight } = getDropdownPosWithTrigger(
                  ref.current,
                );
                openFieldItemSelect({
                  pos: {
                    ...pos,
                    maxHeight: modifiedHeight || undefined,
                  },
                  modalState: {
                    ...child,
                    valueIndex: values.length,
                  },
                });
                closeMenu();
                setSubMenuStatus(null);
              },
            }))
          : undefined,
        subMenuOptions: {
          isOpen: subMenuStatus === visibleField.columnKey,
          openMenu: () => setSubMenuStatus(visibleField.columnKey),
          closeMenu: () => setSubMenuStatus(null),
        },
      };
    };

    const mapSection = (fields: readonly FullColumnKey[]) =>
      fields
        .filter((field) => !disAllowFilter?.includes(field))
        .map((field) => mapField(fieldOptions[field]))
        .filter((field): field is MenuItemProps => Boolean(field));

    const firstSection = mapSection(sectionKeys.firstSection);
    const engagementSection = mapSection(sectionKeys.engagementSection);
    const managerSection = mapSection(sectionKeys.managerSection);
    const sections = [firstSection, engagementSection, managerSection].filter(
      (section) => section.length > 0,
    );

    return sections.flatMap((section, index) => [
      ...(index > 0
        ? ([
            {
              label: 'divider',
              type: MenuItemType.DIVIDER,
            },
          ] as const)
        : []),
      ...section,
    ]);
  }, [
    subMenuStatus,
    ref,
    openFieldItemSelect,
    values.length,
    disAllowFilter,
    closeMenu,
    setSubMenuStatus,
    fieldOptions,
    showTwitterMetrics,
    hiddenLeafIdSet,
  ]);

  return menuOptions;
};
